import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { TenantSummary } from '@forq/contracts';
import { EmptyState, Tabs, type TabItem, WorkspacePage } from '@forq/ui';
import type { SalesGateway } from '@forq/sales-data';
import type { FinanceGateway, IncomingInvoiceGateway } from '@forq/finance-data';
import type { WarehouseGateway } from '@forq/warehouse-data';
import type { ServiceGateway } from '@forq/service-data';
import type { RentalGateway } from '@forq/rental-data';
import type { MachinesGateway } from '@forq/machines-data';
import { resolvePeriod, todayIso, type ResolvedPeriod } from './period';
import { PeriodFilter } from './PeriodFilter';
import { OverviewSection } from './OverviewSection';
import { SalesControllingView } from './SalesControllingView';
import { FinanceSection } from './FinanceSection';
import { WarehouseSection } from './WarehouseSection';
import { MachinesSection } from './MachinesSection';
import { ServiceSection } from './ServiceSection';
import { RentalSection } from './RentalSection';

type SectionId = 'overview' | 'sales' | 'finance' | 'warehouse' | 'machines' | 'service' | 'rental';

interface SectionDef {
  readonly id: SectionId;
  readonly slug: string;
  readonly feature: string;
  readonly label: string;
}

const SECTIONS: readonly SectionDef[] = [
  { id: 'overview', slug: 'uebersicht', feature: 'auswertung.overview', label: 'Übersicht' },
  { id: 'sales', slug: 'vertrieb', feature: 'auswertung.sales', label: 'Vertrieb' },
  { id: 'finance', slug: 'finanzen', feature: 'auswertung.finance', label: 'Finanzen' },
  { id: 'warehouse', slug: 'lager', feature: 'auswertung.warehouse', label: 'Lager' },
  { id: 'machines', slug: 'maschinen', feature: 'auswertung.machines', label: 'Maschinen' },
  { id: 'service', slug: 'service', feature: 'auswertung.service', label: 'Service' },
  { id: 'rental', slug: 'mietpark', feature: 'auswertung.rental', label: 'Mietpark' },
];

export interface AuswertungPageProps {
  readonly tenant: TenantSummary;
  readonly mode: 'demo' | 'supabase';
  readonly permissions: ReadonlySet<string>;
  readonly enabledFeatureIds: readonly string[];
  readonly salesGateway: SalesGateway;
  readonly financeGateway: FinanceGateway;
  readonly incomingGateway: IncomingInvoiceGateway;
  readonly warehouseGateway: WarehouseGateway;
  readonly serviceGateway: ServiceGateway;
  readonly rentalGateway: RentalGateway;
  readonly machinesGateway: MachinesGateway;
}

/**
 * Reporting-Hub: eine Sektion je Fachdomäne (deep-linkbar über /auswertung/:section) mit globalem
 * Zeitraumfilter. Die Sektionen rechnen aus den vorhandenen Daten-Paketen; CSV/PDF je Sektion.
 */
export function AuswertungPage({
  tenant, mode, permissions, enabledFeatureIds,
  salesGateway, financeGateway, incomingGateway, warehouseGateway, serviceGateway, rentalGateway, machinesGateway,
}: AuswertungPageProps) {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const [period, setPeriod] = useState<ResolvedPeriod>(() => resolvePeriod('last12', todayIso()));

  const available = SECTIONS.filter((section) => enabledFeatureIds.includes(section.feature));
  const active: SectionId = available.find((section) => section.slug === slug)?.id ?? available[0]?.id ?? 'overview';
  const tabs: readonly TabItem[] = available.map((section) => ({ id: section.id, label: section.label }));

  return (
    <WorkspacePage
      customizeKey="auswertung"
      eyebrow={`FORQ Auswertung · ${mode === 'demo' ? 'Demo' : 'Supabase'}`}
      title="Auswertung"
      description="Kennzahlen aller Bereiche an einem Ort – mit Zeitraumfilter und Export je Bereich (CSV/PDF)."
      toolbar={(
        <div className="auswertung__toolbar">
          <Tabs
            items={tabs}
            activeId={active}
            onSelect={(id) => navigate(`/auswertung/${SECTIONS.find((section) => section.id === id)?.slug ?? 'uebersicht'}`)}
            ariaLabel="Auswertungsbereich"
          />
          <PeriodFilter period={period} onChange={setPeriod} />
        </div>
      )}
    >
      {available.length === 0 ? (
        <EmptyState eyebrow="Auswertung" title="Keine Auswertung freigegeben." description="Für keinen Bereich besteht ein Sichtrecht." />
      ) : active === 'overview' ? (
        <OverviewSection
          tenantId={tenant.id}
          tenantName={tenant.name}
          period={period}
          permissions={permissions}
          salesGateway={salesGateway}
          financeGateway={financeGateway}
          incomingGateway={incomingGateway}
          warehouseGateway={warehouseGateway}
          serviceGateway={serviceGateway}
          rentalGateway={rentalGateway}
          machinesGateway={machinesGateway}
        />
      ) : active === 'sales' ? (
        <SalesControllingView tenantId={tenant.id} gateway={salesGateway} period={period} tenantName={tenant.name} />
      ) : active === 'finance' ? (
        <FinanceSection tenantId={tenant.id} financeGateway={financeGateway} incomingGateway={incomingGateway} period={period} tenantName={tenant.name} />
      ) : active === 'warehouse' ? (
        <WarehouseSection tenantId={tenant.id} gateway={warehouseGateway} period={period} tenantName={tenant.name} />
      ) : active === 'machines' ? (
        <MachinesSection tenantId={tenant.id} gateway={machinesGateway} period={period} tenantName={tenant.name} />
      ) : active === 'service' ? (
        <ServiceSection tenantId={tenant.id} gateway={serviceGateway} period={period} tenantName={tenant.name} />
      ) : active === 'rental' ? (
        <RentalSection tenantId={tenant.id} gateway={rentalGateway} period={period} tenantName={tenant.name} />
      ) : null}
    </WorkspacePage>
  );
}
